import type {
	ReplyDelay,
	SocialActionType,
	SocialPlatform,
	SocialRule,
	SocialRuleChannelScope
} from './types';

/**
 * Action order. Used for the filter tabs AND for the order the grouped table renders in, so the
 * page reads the same way whichever tab is active.
 */
export const SOCIAL_ACTION_TYPES: SocialActionType[] = [
	'auto_close',
	'auto_flag',
	'auto_reply',
	'auto_hide',
	'auto_delete'
];

export const SOCIAL_ACTION_LABELS: Record<SocialActionType, string> = {
	auto_close: 'Auto-Close',
	auto_flag: 'Auto-Flag',
	auto_reply: 'Auto-Reply',
	auto_hide: 'Auto-Hide',
	auto_delete: 'Auto-Delete'
};

/**
 * Actions that take something away from the commenter and cannot be undone from here.
 *
 * Hiding or deleting a comment removes a customer's words from a public thread. The UI leans on
 * this wherever the consequence of a setting is bigger for them than for a close or a tag — an
 * unscoped rule is a much larger claim when the action is a delete.
 */
export const IRREVERSIBLE_ACTION_TYPES: SocialActionType[] = ['auto_hide', 'auto_delete'];

export const isIrreversibleAction = (actionType: SocialActionType): boolean =>
	IRREVERSIBLE_ACTION_TYPES.includes(actionType);

export type SocialRuleFilter = 'all' | SocialActionType;

export const SOCIAL_RULE_FILTERS: { label: string; value: SocialRuleFilter }[] = [
	{ label: 'All', value: 'all' },
	...SOCIAL_ACTION_TYPES.map((actionType) => ({ label: SOCIAL_ACTION_LABELS[actionType], value: actionType }))
];

/** Section heading above each group of rules, e.g. "When to Auto-Close". */
export const groupTitle = (actionType: SocialActionType): string => `When to ${SOCIAL_ACTION_LABELS[actionType]}`;

/**
 * Group rules by action, dropping empty groups.
 *
 * Rules arrive in priority order from the server and that order is preserved inside each group —
 * priority is what decides which single rule executes when several match, so showing them in any
 * other order would misrepresent the engine.
 */
export const groupRulesByAction = (rules: SocialRule[]): { actionType: SocialActionType; rules: SocialRule[] }[] =>
	SOCIAL_ACTION_TYPES.map((actionType) => ({
		actionType,
		rules: rules.filter((rule) => rule.actionType === actionType)
	})).filter((group) => group.rules.length > 0);

/** Case-insensitive match on the parts of a rule a person would search for. */
export const matchesSearch = (rule: SocialRule, query: string): boolean => {
	const needle = query.trim().toLowerCase();
	if (!needle) return true;
	return rule.name.toLowerCase().includes(needle) || rule.condition.toLowerCase().includes(needle);
};

/** How many channel chips fit on a row before the rest collapse into a "+N". */
export const VISIBLE_CHANNEL_CHIPS = 3;


/**
 * The channel filter's two axes. They intersect: picking Instagram and then two handles shows the
 * rules scoped to those two accounts, not every Instagram account.
 *
 * Empty means "everything". A brand that has ticked nothing is not asking to see nothing, and a
 * filter that empties the page by default reads as a broken list rather than a filter.
 */
export interface ChannelFilterValue {
	platforms: SocialPlatform[];
	channelIds: string[];
}

export interface FilterableChannel {
	channelId: string;
	handle: string;
	/** The account's display name, e.g. "Acme Shop". Empty for an account Meta gave us no name for. */
	name: string;
	platform: SocialPlatform;
}

/**
 * What to call a connected account on screen.
 *
 * The name, falling back to the handle. Facebook Pages are the reason this exists: machine stores
 * `handle = page.username || page.id`, and a Page without a username ends up with its numeric id
 * as its handle — so showing the handle alone renders "1000000000001" where the integrations
 * page shows "Acme Shop".
 */
export const channelLabel = (channel: { name?: string; handle: string; channelId: string }): string =>
	channel.name?.trim() || channel.handle?.trim() || channel.channelId;

/**
 * The secondary line: the handle, but only when it adds something. A Page whose handle is just its
 * id repeats the id back at the reader, and a channel whose handle IS its name says it twice.
 */
export const channelSubLabel = (channel: { name?: string; handle: string; channelId: string }): string | undefined => {
	const handle = channel.handle?.trim();
	if (!handle || handle === channel.channelId || handle === channel.name?.trim()) return undefined;
	return `@${handle}`;
};

export const EMPTY_CHANNEL_FILTER: ChannelFilterValue = { platforms: [], channelIds: [] };

/**
 * Does a rule survive the channel filter?
 *
 * An empty `channelScope` means the rule applies to EVERY connected channel, so it matches any
 * filter — narrowing to one account must not hide the rules that also act on it. A rule matches if
 * ANY of its channels does, since one rule can span several.
 *
 * A channel that is no longer connected falls back to the platform recorded on the scope itself.
 * Otherwise its rules would vanish under every platform filter rather than just the excluding ones.
 */
export const matchesChannelFilter = (
	channelScope: SocialRuleChannelScope[],
	value: ChannelFilterValue,
	channels: FilterableChannel[]
): boolean => {
	if (value.platforms.length === 0 && value.channelIds.length === 0) return true;
	if (channelScope.length === 0) return true;

	return channelScope.some((scope) => {
		const platform = channels.find((channel) => channel.channelId === scope.channelId)?.platform ?? scope.platform;
		const platformOk = value.platforms.length === 0 || value.platforms.includes(platform);
		const accountOk = value.channelIds.length === 0 || value.channelIds.includes(scope.channelId);
		return platformOk && accountOk;
	});
};

/**
 * The longest reply delay, in minutes. Enforced server-side too.
 *
 * It was originally bounded by Meta's 7-day private-reply window; with the DM path removed it is
 * simply how long a comment may sit unanswered in public before a reply stops reading as one.
 */
export const MAX_REPLY_DELAY_MINUTES = 120;

const MINUTES_PER_HOUR = 60;

/** What the picker shows when a delay is switched on with nothing stored yet. */
export const DEFAULT_REPLY_DELAY_MINUTES = 5;

/**
 * One-click delays in the Delay Reply modal, in minutes.
 *
 * Shortcuts, not the full set: any whole number of minutes up to the ceiling can be set with the
 * hours and minutes fields. 15, 30, 60 and 120 stay because they were the only values offered
 * before the picker, so every delay already saved still has a chip.
 */
export const REPLY_DELAY_PRESETS: readonly ReplyDelay[] = [1, 2, 5, 10, 15, 30, 60, 120];

/** A stored 0 means the same as nothing stored: replies go out immediately. */
export const hasReplyDelay = (delay: ReplyDelay | null | undefined): delay is ReplyDelay =>
	typeof delay === 'number' && delay > 0;

export const clampReplyDelay = (minutes: number): ReplyDelay =>
	Math.min(MAX_REPLY_DELAY_MINUTES, Math.max(0, Math.round(minutes)));

export const splitReplyDelay = (delay: ReplyDelay): { hours: number; minutes: number } => ({
	hours: Math.floor(delay / MINUTES_PER_HOUR),
	minutes: delay % MINUTES_PER_HOUR
});

/** "2 min", "1 hr", "1 hr 30 min". */
export const formatReplyDelay = (delay: ReplyDelay): string => {
	const { hours, minutes } = splitReplyDelay(delay);
	const parts = [hours > 0 ? `${hours} hr` : '', minutes > 0 ? `${minutes} min` : ''].filter(Boolean);

	return parts.length > 0 ? parts.join(' ') : '0 min';
};

const WHOLE_NUMBER = /^\d+$/;

const readWholeNumber = (text: string): number | null => (WHOLE_NUMBER.test(text.trim()) ? Number(text.trim()) : null);

/**
 * Why the hours and minutes fields cannot be saved as they stand, or null when they can.
 *
 * Returned as the sentence the dialog shows, so the rule and its wording cannot drift apart.
 */
export const replyDelayProblem = (hoursText: string, minutesText: string): string | null => {
	const hours = readWholeNumber(hoursText);
	const minutes = readWholeNumber(minutesText);

	if (hours === null || minutes === null || minutes >= MINUTES_PER_HOUR) {
		return 'Enter whole hours, and minutes from 0 to 59.';
	}

	const total = hours * MINUTES_PER_HOUR + minutes;
	if (total > MAX_REPLY_DELAY_MINUTES) return `The longest delay is ${formatReplyDelay(MAX_REPLY_DELAY_MINUTES)}.`;
	if (total === 0) return 'Pick a delay, or switch this off.';

	return null;
};

/** The delay the two fields spell, or null when they do not spell a savable one. */
export const replyDelayFromFields = (hoursText: string, minutesText: string): ReplyDelay | null => {
	if (replyDelayProblem(hoursText, minutesText) !== null) return null;

	return Number(hoursText.trim()) * MINUTES_PER_HOUR + Number(minutesText.trim());
};

/** Short form for the button, e.g. "Delay reply · 30 min". */
export const delayButtonLabel = (delay: ReplyDelay | null): string =>
	hasReplyDelay(delay) ? `Delay reply · ${formatReplyDelay(delay)}` : 'Delay reply';

/** "Instagram Comment" — the heading above the thread in the detail panel. */
export const platformCommentLabel = (platform: SocialPlatform): string =>
	`${platform === 'instagram' ? 'Instagram' : 'Facebook'} Comment`;

/**
 * What an empty channel selection actually does.
 *
 * The form used to say "the rule will be applied only on these selected channels" regardless of
 * what was picked, so with nothing selected it read as "applies to nowhere" — the exact opposite
 * of the behaviour. An unscoped rule matches EVERY channel, and because the scope is re-read on
 * every comment rather than resolved when the rule is saved, it also picks up channels connected
 * later. Someone leaving the field blank because they thought it made the rule inert would get the
 * broadest possible rule instead.
 *
 * Spelled out hardest for hide and delete: those take a customer's words off a public thread, so
 * "all channels, including ones that do not exist yet" is a far larger claim there than it is on a
 * close or a tag.
 */
export const channelScopeHint = (selectedCount: number, actionType: SocialActionType): string => {
	if (selectedCount > 0) return 'Applies only to the selected channels.';

	return isIrreversibleAction(actionType)
		? `Applies to all channels — including any you connect later. ${SOCIAL_ACTION_LABELS[actionType]} removes a comment from public view, so pick channels to limit where it runs.`
		: 'Applies to all channels — including any you connect later. Pick channels to narrow it.';
};
